import { Outlet, Link, useLocation } from 'react-router-dom';
import { FileText, Wrench, Sparkles, LogIn, User as UserIcon, LogOut, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';

export default function Layout() {
  const { pathname } = useLocation();
  const { user, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <header className="sticky top-0 z-40 backdrop-blur bg-stone-50/80 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-xl bg-stone-900 grid place-items-center text-orange-400 group-hover:rotate-3 transition-transform">
              <FileText size={18} strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-lg tracking-tight">Everything PDF</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Forms · Docs · Tools</div>
            </div>
          </Link>

          <nav className="flex items-center gap-1 text-sm">
            <Link
              to="/"
              data-testid="nav-templates"
              className={`hidden sm:inline-block px-3 py-2 rounded-full transition ${pathname === '/' ? 'bg-stone-900 text-white' : 'hover:bg-stone-200/60 text-stone-700'}`}
            >Templates</Link>
            <Link
              to="/tools"
              data-testid="nav-tools"
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full transition ${pathname.startsWith('/tools') ? 'bg-stone-900 text-white' : 'hover:bg-stone-200/60 text-stone-700'}`}
            >
              <Wrench size={14} /> Tools
            </Link>
            <Link
              to="/pricing"
              data-testid="nav-pricing"
              className={`px-3 py-2 rounded-full transition ${pathname === '/pricing' ? 'bg-stone-900 text-white' : 'hover:bg-stone-200/60 text-stone-700'}`}
            >Pricing</Link>
            <Link
              to="/blog"
              data-testid="nav-blog"
              className={`hidden sm:inline-block px-3 py-2 rounded-full transition ${pathname.startsWith('/blog') ? 'bg-stone-900 text-white' : 'hover:bg-stone-200/60 text-stone-700'}`}
            >Blog</Link>

            {!user && (
              <button
                onClick={() => login(window.location.pathname + window.location.search)}
                data-testid="header-login-btn"
                className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-orange-600 text-white text-sm font-medium btn-clay"
              >
                <LogIn size={14} /> Sign in
              </button>
            )}

            {user && (
              <div className="ml-2 relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white border border-stone-200 hover:border-stone-300 transition"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-stone-200 grid place-items-center text-stone-600">
                      <UserIcon size={14} />
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium truncate max-w-[120px]">{user.name || user.email}</span>
                  {user.is_pro && <Crown size={12} className="text-orange-500" />}
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-stone-200 shadow-lg p-2 z-50" data-testid="user-menu">
                    <div className="px-3 py-2 border-b border-stone-100 mb-1">
                      <div className="text-xs text-stone-500">Signed in as</div>
                      <div className="text-sm font-medium truncate">{user.email}</div>
                      <div className="text-[10px] uppercase tracking-wider mt-1">
                        {user.is_pro
                          ? <span className="text-orange-600 font-bold">{user.plan === 'lifetime' ? 'Lifetime' : 'Pro'}</span>
                          : <span className="text-stone-500">Free</span>}
                      </div>
                    </div>
                    <Link
                      to="/account"
                      onClick={() => setMenuOpen(false)}
                      data-testid="menu-account"
                      className="block px-3 py-2 rounded-lg text-sm hover:bg-stone-100"
                    >Account</Link>
                    {user.is_admin && (
                      <Link
                        to="/admin"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-admin"
                        className="block px-3 py-2 rounded-lg text-sm hover:bg-stone-100 text-stone-700"
                      >Admin dashboard</Link>
                    )}
                    {!user.is_pro && (
                      <Link
                        to="/pricing"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-upgrade"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-stone-100 text-orange-600 font-medium"
                      ><Sparkles size={14} /> Upgrade to Pro</Link>
                    )}
                    <button
                      onClick={() => { setMenuOpen(false); logout(); }}
                      data-testid="menu-logout"
                      className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-stone-100 text-stone-700"
                    ><LogOut size={14} /> Sign out</button>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-bold mb-3">Popular templates</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/free-nda-template" className="text-stone-600 hover:text-orange-600">Free NDA</Link></li>
                <li><Link to="/free-invoice-template" className="text-stone-600 hover:text-orange-600">Invoice generator</Link></li>
                <li><Link to="/free-resume-template" className="text-stone-600 hover:text-orange-600">Resume builder</Link></li>
                <li><Link to="/free-rental-agreement-template" className="text-stone-600 hover:text-orange-600">Rental agreement</Link></li>
                <li><Link to="/free-freelance-contract-template" className="text-stone-600 hover:text-orange-600">Freelance contract</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-bold mb-3">Business</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/free-quote-template" className="text-stone-600 hover:text-orange-600">Quote / Estimate</Link></li>
                <li><Link to="/free-receipt-template" className="text-stone-600 hover:text-orange-600">Payment receipt</Link></li>
                <li><Link to="/free-purchase-order-template" className="text-stone-600 hover:text-orange-600">Purchase order</Link></li>
                <li><Link to="/free-sow-template" className="text-stone-600 hover:text-orange-600">Statement of Work</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-bold mb-3">Personal & HR</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/free-cover-letter-template" className="text-stone-600 hover:text-orange-600">Cover letter</Link></li>
                <li><Link to="/free-resignation-letter-template" className="text-stone-600 hover:text-orange-600">Resignation letter</Link></li>
                <li><Link to="/free-offer-letter-template" className="text-stone-600 hover:text-orange-600">Offer letter</Link></li>
                <li><Link to="/free-reference-letter-template" className="text-stone-600 hover:text-orange-600">Reference letter</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-bold mb-3">Product</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-stone-600 hover:text-orange-600">All templates</Link></li>
                <li><Link to="/tools" className="text-stone-600 hover:text-orange-600">PDF tools</Link></li>
                <li><Link to="/blog" className="text-stone-600 hover:text-orange-600">Blog</Link></li>
                <li><Link to="/pricing" className="text-stone-600 hover:text-orange-600">Pricing</Link></li>
                <li><Link to="/account" className="text-stone-600 hover:text-orange-600">Account</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-200 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-stone-500">
            <div className="font-display tracking-tight">© {new Date().getFullYear()} Everything PDF</div>
            <span>Made for paperwork that doesn't suck.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
